import { DataGrid } from "@mui/x-data-grid";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { toast } from "../../../components/AppToast";
import { Button, Checkbox } from "@mui/material";
import { formatToDisplayDate } from "../../../utils/dateFormatter";
 
const CreateUserForm = ({
  initialValues,
  onSubmit,
  submitText = "Register",
  cancelText = "",
  onCancel = () => { },
  partners,
  facilityData,
  roleData,
  openCreateUser = false,
  partnerId: tabPartnerId,
  readOnly = false,
  type,
  isEdit
}) => {
    const { userDetail } = useSelector((state) => state.login);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [partnersOptions, setPartnersOptions] = useState([]);
  const { tenantId } = useSelector((state) => state?.login?.userDetail);
  const [updatedRoles, setUpdatedRoles] = useState([]);
  const [updatedFacilityData, setUpdatedFacility] = useState([]);
  const [roleFacilityList, setRoleFacilityList] = useState([]);
  const [editingRow, setEditingRow] = useState(null);

  const today = new Date().toISOString().split("T")[0];
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedFacility, setSelectedFacility] = useState("");
  const [accessStartDate, setAccessStartDate] = useState(today);
  const [accessEndDate, setAccessEndDate] = useState("");
  const [alreadySaved, setAlreadySaved] = useState(false);
  const [defaultWhId, setDefaultWhId] = useState(null);
  const rowHeight = 42;
  const headerHeight = 42;

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  // ------------------- User Fields -------------------
  const userFields = [
    { name: "firstName", label: "First Name", required: true },
    { name: "lastName", label: "Last Name", required: true },
    { name: "username", label: "User Name", required: true },
    { name: "email", label: "Email", type: "email", required: true },
    { name: "mobile", label: "Mobile", type: "number", required: true },
    // { name: "password", label: "Password", type: "password", required:(!isEdit) },
    ...(!isEdit
      ? [
        {
          name: "password",
          label: "Password",
          type: "password",
          required: true,
        },
      ]
      : []),

    {
      name: "tenantUserType",
      label: "User Type",
      type: "select",
      options: [
        { label: "Internal", value: "INTERNAL" },
        { label: "External", value: "EXTERNAL" },
      ],
      required: true,
      hide:userDetail.userType=='PARTNER'?true:false
     
    },

    {
      name: "partnerId",
      label: "Organization Name",
      type: "select",
      options: partnersOptions,
      required: true,
      isDisabled: (formData) => formData?.tenantUserType === "INTERNAL",
    },

  ];

  // ------------------- Utility -------------------
  const toInputDateFormat = (dateStr) => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      const [dd, mm, yyyy] = dateStr.split("/");
      return `${yyyy}-${mm}-${dd}`;
    }
    return "";
  };

  const formatDate = (d) => {
    const D = new Date(d);
    const y = D.getFullYear();
    const m = String(D.getMonth() + 1).padStart(2, "0");
    const day = String(D.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const nextYearFrom = (dateLike) => {
    const base = dateLike ? new Date(dateLike) : new Date();
    const d = new Date(base);
    d.setFullYear(d.getFullYear() + 25);
    return formatDate(d);
  };

  // ------------------- Initial Setup -------------------
  useEffect(() => {
    let initialForm = userFields.reduce((acc, field) => {
      let value = initialValues?.[field.name] ?? "";
      if (field.type === "date" && value) value = toInputDateFormat(value);
      acc[field.name] = value;
      return acc;
    }, {});


    if (initialForm.tenantUserType === "INTERNAL") initialForm.partnerId = tenantId?._id;
      
    if (openCreateUser && tabPartnerId) {
      initialForm.tenantUserType = "EXTERNAL";
      initialForm.partnerId = tabPartnerId;
    }

    

    const roleAccess = initialValues?.roleFacilityAccess?.map((user) => ({
      ...user,
      accessStartDate: formatDate(user.accessStartDate),
      accessEndDate: formatDate(user.accessEndDate),
      alreadySave: true
    }));

    if(roleAccess){

      const defaultRow = roleAccess.find(row => row.defaultWh); 
  if (defaultRow) {
    setDefaultWhId(defaultRow.id); 
  }
}

    if (roleAccess) setRoleFacilityList(roleAccess);
    setFormData(initialForm);
    if(userDetail.userType=='PARTNER' && !isEdit && !readOnly ){
      initialForm.tenantUserType = "EXTERNAL";
      setFormData({ ...formData, tenantUserType: 'EXTERNAL', partnerId: "" });
      setUpdatedRoles(roleData.filter((role) => role.roleType === "EXTERNAL"));
    }
  }, [openCreateUser, tabPartnerId]);

  useEffect(() => {
    if (!formData?.tenantUserType) {
      setUpdatedRoles(roleData || []);
      setUpdatedFacility(facilityData || []);
      return;
    }

    if (formData.tenantUserType === "INTERNAL") {
      setUpdatedRoles(roleData?.filter((r) => r.roleType === "INTERNAL") || []);
    } else if (formData.tenantUserType === "EXTERNAL") {
      setUpdatedRoles(roleData?.filter((r) => r.roleType === "EXTERNAL") || []);
    }
  }, [formData?.tenantUserType, roleData, facilityData]);

  useEffect(() => {
    if (formData?.tenantUserType !== "EXTERNAL") {
      setPartnersOptions([{ label: tenantId?.tenant_name, value: tenantId?._id }]);
    } else {
      setPartnersOptions(partners || []);
    }
  }, [formData?.tenantUserType, partners]);

  useEffect(() => {
  
  if(roleFacilityList.some(r=>r.defaultWh==true)){
    const roleData = roleFacilityList.find(f=>{return f.defaultWh==true});
      formData.role=roleData.roleId;
  }
  
}, [roleFacilityList]);


  // ------------------- Validation -------------------
  const validate = () => {
    const newErrors = {};

    userFields.forEach((field) => {
      if (
        field.required &&
        (!formData[field.name] || formData[field.name].toString().trim() === "")
      ) {
        newErrors[field.name] = `${field.label} is required`;
      }

      if (field.name === "email" && formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
        newErrors.email = "Invalid email format";
      }

      if (field.name === "mobile" && formData.mobile && !/^[6-9]\d{9}$/.test(formData.mobile)) {
        newErrors.mobile = "Mobile must be a valid 10-digit number";
      }

      if (field.name === "password" && type === "create") {
        if (!formData.password || formData.password.length < 6) {
          newErrors.password = "Password must be at least 6 characters";
        }
      }
    });

    if (!roleFacilityList.length) {
      newErrors.roleFacility = "Add at least one Role & Facility mapping";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ------------------- Handlers -------------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "tenantUserType") {
      if (value === "EXTERNAL") {
        if (!partners.length) {
          toast.error("No partner onboarded. Add a partner first.");
          return;
        }
        setFormData({ ...formData, tenantUserType: value, partnerId: "" });
        setUpdatedRoles(roleData.filter((role) => role.roleType === "EXTERNAL"));
      } else {
        setFormData({ ...formData, tenantUserType: value, partnerId: tenantId?._id });
        setUpdatedRoles(roleData.filter((role) => role.roleType === "INTERNAL"));
      }

      setErrors((prev) => ({ ...prev, [name]: "" }));

      return;
    }

    if (name === "partnerId") {
      setFormData({ ...formData, partnerId: value });
      setUpdatedRoles(
        roleData.filter((role) => role.roleType === formData.tenantUserType && role.partnerId === value)
      );
    }

    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    onSubmit({
      ...formData,
      countryCode: "+91",
      tenantId: tenantId?._id,
      roleFacilityAccess: roleFacilityList,
    });
  };

  // ------------------- Role-Facility -------------------
  const handleAddOrUpdate = () => {
    const newErrors = {};
    if (!selectedRole) newErrors.role = "Role is required";
    if (!selectedFacility) newErrors.facility = "Facility is required";
    if (accessEndDate && new Date(accessEndDate) <= new Date(accessStartDate)) {
      newErrors.endDate = "End date must be after start date";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const newRow = {
      id: editingRow ?? (roleFacilityList.length ? roleFacilityList[roleFacilityList.length - 1].id + 1 : 1),
      roleId: selectedRole,
      roleName: updatedRoles.find((r) => r.value === selectedRole)?.label || "",
      facilityId: selectedFacility,
      facilityName: updatedFacilityData.find((f) => f.value === selectedFacility)?.label || "",
      accessStartDate,
      accessEndDate: accessEndDate || nextYearFrom(accessStartDate),
    };

    if (editingRow !== null) {
      setRoleFacilityList((prev) =>
        prev.map((item) => (item.id === editingRow ? newRow : item))
      );
    } else {
      setRoleFacilityList((prev) => [...prev, newRow]);
    }

    resetRoleFields();
  };

  const resetRoleFields = () => {
    setSelectedRole("");
    setSelectedFacility("");
    setAccessStartDate(today);
    setAccessEndDate("");
    setEditingRow(null);
  };

  const handleEditRoleFacility = (row) => {
    const savedData = row.alreadySave || false;
    setAlreadySaved(savedData)
    setSelectedRole(row.roleId);
    setSelectedFacility(row.facilityId);
    setAccessStartDate(row.accessStartDate);
    setAccessEndDate(row.accessEndDate);
    setEditingRow(row.id);
  };

  const handleCancelEdit = () => {
    setSelectedRole("");
    setSelectedFacility("");
    setAccessStartDate("");
    setAccessEndDate("");
    setEditingRow(null);
    setErrors({});
  };

  const handleRemoveRoleFacility = (id) => {
    setRoleFacilityList((prev) => prev.filter((item) => item.id !== id));
  };

  // ------------------- Columns -------------------
  const roleFacilityColumns = [
    { field: "roleName", headerName: "Role", width: 200 },
    { field: "facilityName", headerName: "Facility", width: 200 },
    { field: "accessStartDate", headerName: "Access Start Date", width: 180 },
    { field: "accessEndDate", headerName: "Access End Date", width: 180 },
    {
      field: "actions",
      headerName: "Actions",
      width: 150,
      renderCell: (params) => (
        <>
          <Button size="small" onClick={() => handleEditRoleFacility(params.row)}>Edit</Button>
          <Button size="small" color="error" onClick={() => handleRemoveRoleFacility(params.row.id)}>Delete</Button>
        </>
      ),
    },
    {field:'defaultWh',headerName:'Default Wh',width:130,
       renderCell: (params) => (
        <>
          <Checkbox onClick={()=>handleDefaultCheckbox(params.row)}
            checked={defaultWhId  == params.row.id}></Checkbox>
        </>
      ),
    }
  ];



const handleDefaultCheckbox = (rowData) => {
  setRoleFacilityList((prev) => {
    const isAlreadySelected = rowData.id === defaultWhId;

    // Case 1: unselect (make all false)
    if (isAlreadySelected) {
      setDefaultWhId(null);
      return prev.map(row => ({
        ...row,
        defaultWh: false
      }));
    }

    // Case 2: select one (only one true)
    setDefaultWhId(rowData.id);
    return prev.map(row => ({
      ...row,
      defaultWh: row.id === rowData.id
    }));
  });

  
  
};


  // ------------------- JSX -------------------
  return (
    <form onSubmit={handleSubmit}>
      <div className="container">
        <div className="row gx-5 gy-0">
          {userFields.map((field) => {
            if (field.conditional && !field.conditional(formData)) return null;

            const isFieldDisabled =
              readOnly ||
              (isEdit &&
                ["partnerId", "tenantUserType", "username", "email"].includes(field.name)) ||
              field.isDisabled?.(formData);


            return (
              <>
               {field.hide!==true && ( <div key={field.name} className="col-md-3" >
                <label className="form-label fw-semibold small">
                  {field.label} {field.required && <span className="required">*</span>}
                </label>

                {field.type === "select"  ? (
                  <select
                    
                    className={`form-select form-select-sm ${errors[field.name] ? "is-invalid" : ""}`}
                    name={field.name}
                    value={formData[field.name] || ""}
                    onChange={handleChange}
                    // required={field.required}
                    disabled={isFieldDisabled}
                  >
                    <option value="">Select {field.label}</option>
                    {field.options?.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type || "text"}
                    className={`form-control form-control-sm ${errors[field.name] ? "is-invalid" : ""}`}
                    name={field.name}
                    value={formData[field.name] || ""}
                    onChange={handleChange}
                    // required={field.required}
                    disabled={isFieldDisabled}
                  />
                )}

                {errors[field.name] && <small className="text-danger">{errors[field.name]}</small>}
              </div>)}
             
</>

            );
          })}
        </div>

        {/* Role & Facility Section */}
        <div className="mt-4">
          <h6>Assigned Roles & Facilities</h6>
          {errors.roleFacility && <small className="text-danger d-block mb-2">{errors.roleFacility}</small>}

          <div className="row row-cols-1 row-cols-md-5 g-3 userTableRole">
            {/* Role */}
            <div className="col belowSpace">
              <label className="form-label fw-semibold">Role</label>
              <select
                className={`form-select form-select-sm ${errors.role ? "is-invalid" : ""}`}
                value={selectedRole}
                onChange={(e) => {
                  setSelectedRole(e.target.value);
                  setErrors({ ...errors, role: "" });
                }}
              >
                <option value="">Select Role</option>
                {updatedRoles.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}
              </select>
              {errors.role && <small className="text-danger">{errors.role}</small>}
            </div>

            {/* Facility */}
            <div className="col belowSpace">
              <label className="form-label fw-semibold">Facility</label>
              <select
                className={`form-select form-select-sm ${errors.facility ? "is-invalid" : ""}`}
                value={selectedFacility}
                onChange={(e) => {
                  setSelectedFacility(e.target.value);
                  setErrors({ ...errors, facility: "" });
                }}
              >
                <option value="">Select Facility</option>
                {updatedFacilityData.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              {errors.facility && <small className="text-danger">{errors.facility}</small>}
            </div>

            {/* Access Dates */}
            <div className="col belowSpace">
              <label className="form-label fw-semibold">Access Start Date</label>
              <input type="date" className="form-control form-control-sm" value={accessStartDate} min={today} onChange={(e) => setAccessStartDate(e.target.value)} disabled={isEdit && alreadySaved} />
            </div>

            <div className="col belowSpace">
              <label className="form-label fw-semibold">Access End Date</label>
              <input type="date" className="form-control form-control-sm" value={accessEndDate} min={accessStartDate} onChange={(e) => setAccessEndDate(e.target.value)} />
              {errors.endDate && <small className="text-danger">{errors.endDate}</small>}
            </div>

            {/* Add/Update Button */}
            {/* <div className="col d-flex align-items-center mt-4">
              <button type="button" className="btn btn-success btn-sm px-4" onClick={handleAddOrUpdate}>
                {editingRow !== null ? "Update" : "Add"}
              </button>

              {editingRow !== null && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm px-3"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
              )}
            </div> */}


            <div className="col d-flex align-items-center justify-content-start mt-4 gap-2">
              <button
                type="button"
                className="btn btn-success btn-sm px-3 py-1 fw-semibold"
                onClick={handleAddOrUpdate}
                style={{ fontSize: "0.8rem" }}
              >
                {editingRow !== null ? "Update" : "Add"}
              </button>

              {editingRow !== null && (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm px-3 py-1 fw-semibold"
                  onClick={handleCancelEdit}
                  style={{ fontSize: "0.8rem" }}
                >
                  Cancel
                </button>
              )}
            </div>

          </div>

          <DataGrid
            rows={roleFacilityList}
            columns={roleFacilityColumns}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25]}
            disableSelectionOnClick
            rowHeight={rowHeight}
            columnHeaderHeight={headerHeight}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            autoHeight
          />
        </div>

        {/* Submit / Cancel */}
        <div className="text-end mt-3 d-flex justify-content-center gap-2">
          {cancelText && (
            <div className="ms-2">
              <button type="button" onClick={onCancel} className="btn btn-secondary">{cancelText}</button>
            </div>
          )}
          {!readOnly && (
            <div className="ms-2">
              <button type="submit" className="btn btn-primary">{submitText}</button>
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

export default CreateUserForm;
